import { useParams, useLocation, Outlet, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import NotFound from "./NotFound";
import { COUNTRY_DATA } from "../utils/countryCode";
import { useEffect } from "react";
import { setCountry } from '../store/slices/sessionSlice';

export default function CountryGuard() {
  const { country } = useParams();
  const sessionCountry = useSelector((state: RootState) => state.session.country);
  const isRehydrated = useSelector((state: any) => state._persist?.rehydrated);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userIsLoggedIn = useSelector((state: any) => !!state.user.token);

  // Top-level normalization: force any uppercase two-letter country code to lowercase
  useEffect(() => {
    const match = location.pathname.match(/^\/([A-Z]{2})(\/|$)/);
    if (match) {
      const lower = match[1].toLowerCase();
      const newPath = location.pathname.replace(/^\/[A-Z]{2}/, `/${lower}`);
      if (location.pathname !== newPath) {
        navigate(newPath, { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  // List of valid country codes (lowercase, e.g., 'in', 'bd', 'vn', etc.)
  const validCountryCodes = Object.values(COUNTRY_DATA).map(opt => opt.country_key);

  // All hooks are above this line. Now do conditional returns:
  if (!isRehydrated) return null;
  if (!country || !validCountryCodes.includes(country.toLowerCase())) {
    return <NotFound />;
  }
  
  // On the login page, we must sync the country from the URL to the session
  // so the login component can display the correct flag and country code.
  useEffect(() => {
    if (!userIsLoggedIn && country && location.pathname.endsWith('/login')) {
      dispatch(setCountry(country.toLowerCase()));
    }
  }, [userIsLoggedIn, country, location.pathname, dispatch]);
  
  // Only allow login page if not logged in or session country matches URL country
  const isLoginPage = location.pathname.endsWith('/login');
  if (isLoginPage) {
    if (!sessionCountry || country.toLowerCase() === sessionCountry.toLowerCase()) {
      return <Outlet />;
    } else {
      return <NotFound />;
    }
  }

  // For any other page, the URL country must match the session country.
  // If they don't match, it's an invalid state.
  if (!sessionCountry || country.toLowerCase() !== sessionCountry.toLowerCase()) {
    return <NotFound />;
  }

  console.log('Country param:', country, 'Session country:', sessionCountry, 'Valid codes:', validCountryCodes);

  return <Outlet />;
} 